import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {BaseCardScratchpadState} from '../../scratchpad/baseCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import getSignalsMetadata from '../../utils/conditions/getSignalsMetadata';
import {ConditionOperatorKey} from 'common/src/modules/conditions/conditions.types';
import {toAiCondOperator} from '../../cardDetailsV2/aiModels/cardDetailsAI.enums';

type SignalAwareScratchpadState = BaseCardScratchpadState & {
  card: BaseCardScratchpadState['card'] & {
    availableSignals?: {value: string; title: string}[];
  };
};

const searchSignalToolSchema = z.object({
  conditionField: z
    .string()
    .describe(
      '1-3 word signal search term (from placement data, DSP data, user data, or previously selected products/locations/content) by which an output will be selected',
    ),
});

export default function searchSignalTool(store: CLStore, nodeType: NodeType = NodeType.CONDITION) {
  async function execute({
    input: {conditionField},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof searchSignalToolSchema,
    SignalAwareScratchpadState
  >): Promise<ToolResult> {
    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolError('Campaign is required to get condition signals');
    }

    if (!versionId) {
      return toolError('Version ID is required to get condition signals');
    }

    const validatedScratchpad = validateAgentScratchpad<SignalAwareScratchpadState>(
      agentScratchpad,
      nodeType,
    );

    const signalsMetadata = await getSignalsMetadata(conditionField, campaign.id, versionId);

    if (signalsMetadata.length === 0) return toolResult('No signals found');

    validatedScratchpad.card.availableSignals = [
      ...(validatedScratchpad.card.availableSignals || []),
      ...signalsMetadata.map((signal) => {
        return {
          value: signal.signalKey,
          title: signal.signalName,
          optionalValues:
            (signal.optionalValues || []).map((optionalValue) => ({
              displayName: optionalValue.displayName,
              value: optionalValue.value,
            })) || [],
          strictOptionalValues: signal.strictOptionalValues,
          optionalOperators: signal.optionalOperators,
          shouldQueryOptionalValues: signal.shouldQueryOptionalValues || false,
          categories: signal.categories.map((category) => category.value),
        };
      }),
    ];

    const enrichedMetadata = signalsMetadata.map((signal) => ({
      ...signal,
      optionalOperators: signal.optionalOperators
        .map((op) => toAiCondOperator(op as ConditionOperatorKey))
        .filter((name): name is string => name != null),
    }));

    return toolResult(`Signals found: ${JSON.stringify(enrichedMetadata)}`);
  }

  return tool({
    name: V2CardAgentToolName.SearchSignal,
    displayName: 'Searching signal',
    description: 'Search for a signal by keyword.',
    execute,
    inputSchema: searchSignalToolSchema,
  });
}
